import { loadStripe } from "@stripe/stripe-js";
/* -----------------Globals--------------- */
import { useEffect, useMemo, useState } from "react";

/* -----------------Helpers & Hooks--------------- */
import { useAppStore } from "@hooks/useAppStore";
import { Stack, useLocalSearchParams } from "expo-router";

/* -----------------Constants--------------- */
import { urls } from "@constants/urls";

import { pageViewTypes } from "@constants/tracking";
import { events } from "@constants/tracking";
import { handleCreateCheckoutSession } from "@features/dashboard/helpers/purchase";
import { handleNavigateBack } from "@helpers/common";
import useAnalytics from "@hooks/useAnalytics";
import useAuthUser from "@hooks/useAuthUser";
import { useProductDetails } from "@hooks/useProduct";
import { isDev, isStaging, stagingBaseUrl } from "@memorang/constants/urls";
import type { ProductType } from "@memorang/types";
import Box from "@memorang/ui/components/Box";
import { CustomAppBar } from "@memorang/ui/components/CustomAppbar";
import Loader from "@memorang/ui/components/Loader";
/* -----------------Stripe--------------- */
import {
	EmbeddedCheckout,
	EmbeddedCheckoutProvider,
} from "@stripe/react-stripe-js";
import DocumentTitle from "components/DocumentTitle";
import { useTranslation } from "react-i18next";

const stripeKey = isDev
	? process.env.EXPO_PUBLIC_STRIPE_KEY_TEST
	: process.env.EXPO_PUBLIC_STRIPE_KEY_LIVE;

if (!stripeKey) {
	throw new Error("Stripe key not found");
}
const stripePromise = loadStripe(stripeKey);

const CheckoutRoute = () => {
	const baseUrl = useAppStore((store) => store.app.links.baseUrl);
	const [clientSecret, setClientSecret] = useState<string | null>(null);

	const memoizedAnalyticsParams = useMemo(() => {
		return {
			type: pageViewTypes.checkout,
			eventName: events.checkoutViewed,
		};
	}, []);
	useAnalytics(memoizedAnalyticsParams);
	const { t } = useTranslation();

	const { userLogicalId } = useAuthUser();

	const { productType, id, planId } = useLocalSearchParams<{
		productType: ProductType;
		id: string;
		planId?: string;
	}>();

	const { data: productDetails } = useProductDetails(
		productType,
		Number.parseInt(id),
	);

	useEffect(() => {
		// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
		const fetchDetailsAndCreateSession = async () => {
			if (id) {
				const numericId = Number.parseInt(id);

				if (productDetails) {
					const productPlans = productDetails.plans;

					const selectedPlan = productPlans.find(
						(plan) => plan.id === Number.parseInt(planId!),
					);

					const routeSegment = `purchase/${numericId}/success?duration=${selectedPlan?.duration}&interval=${selectedPlan?.interval}&amount=${selectedPlan?.amount}&currency=${selectedPlan?.currency}&planId=${selectedPlan?.id}`;
					// const testDomain = 'https://psi-web-vivekneel-memorang.vercel.app';
					const redirectUrl = isDev
						? isStaging
							? `${stagingBaseUrl}/${routeSegment}`
							: `${urls.dev}/${routeSegment}`
						: `${baseUrl}/${routeSegment}`;

					const productDescription =
						"Unlock all items and maximize your test readiness";

					if (selectedPlan && userLogicalId) {
						const res = await handleCreateCheckoutSession({
							selectedPlan,
							productDescription,
							productId: productDetails.id,
							redirectUrl,
							productName: productDetails.name,
							userId: userLogicalId,
						});
						setClientSecret(res);
					}
				}
			}
		};

		fetchDetailsAndCreateSession();
	}, [id, planId, baseUrl, productDetails, userLogicalId]);

	if (!clientSecret) {
		return (
			<Box flex={1} justifyContent="center" alignItems="center" height="100%">
				<Loader />
			</Box>
		);
	}

	return (
		<>
			<DocumentTitle title="Checkout" />
			<Stack.Screen
				options={{
					headerShown: true,
					header: () => {
						return (
							<CustomAppBar
								title={t("purchase.checkout.headerTitle")}
								leftIconType="back"
								onPressBack={() => {
									handleNavigateBack("/(protected)/(tabs)/home");
								}}
							/>
						);
					},
				}}
			/>
			{clientSecret && (
				<EmbeddedCheckoutProvider
					stripe={stripePromise}
					options={{ clientSecret }}
				>
					<EmbeddedCheckout />
				</EmbeddedCheckoutProvider>
			)}
		</>
	);
};

export default CheckoutRoute;
